import React, { useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    Modal,
    Paper, TextField,
    Typography
} from "@material-ui/core";
import {Alert, AlertTitle, Autocomplete} from "@material-ui/lab";
import useStyles from "../../styles/styles";
import useFormInputs from "../../hooks/useFormInputs";
import ConfigFields from "./ConfigFields";
import stateMap from "../../utils/stateMap";
import {useLazyQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";
import {base16AteliersulphurpoolLight} from "react-syntax-highlighter/dist/cjs/styles/prism";
import {CopyToClipboard} from "react-copy-to-clipboard";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RenderInput from "../TrafficSource/RenderInput";
import removeParenthesis from "../../utils/removeParenthesis";

const ConfigStateModal = ({ data, modalData, closeModal }) => {
    const classes = useStyles();
    const [list, setList] = useState([]);
    const [view, setView] = useState('');
    const [id, setId] = useState('');
    const [error, setError] = useState(false);
    const [importType, setImportType] = useState('');
    const [multipleStates, setMultipleStates] = useState([]);
    const [isError, setIsError] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const onSubmit = () => {
        setSubmitLoading(true);
        setIsError(false);
        let error = false;
        const { key, category } = inputs;
        const newData = {
            key,
            category,
        };
        Object.keys(newData).forEach(key => {
            const value = newData[key]
            if (value === '') {
                error = true;
            }
        });
        if (!list.length) {
            error = true;
        }
        if (modalData.data.id) {
            newData.id = modalData.data.id;
        }
        if (!error) {
            if (modalData.showAddRow) {
                modalData.triggerCreate(list, newData);
            } else {
                modalData.triggerUpdate(list, newData);
            }
        } else {
            setSubmitLoading(false);
            setIsError(error);
        }
    };
    const {inputs, handleInputChange, handleSubmit, setInputs} = useFormInputs(onSubmit);

    const [runQuery, {loading, data: stateListData}] = useLazyQuery(gql`
        query ImportStateList(
            $campaignId: String!
            $servicerId: String!
            $portfolioId: String!
        ) {
            campaign(id: $campaignId) {
                id
                name
                filterAttributes(campaignOnly: true) {
                    hierarchyLevel
                    type
                    description
                    attributeType
                }
            }
            servicer(id: $servicerId) {
                id
                name
                filterAttributes {
                    hierarchyLevel
                    type
                    description
                    attributeType
                }
            }
            portfolio(id: $portfolioId) {
                id
                name
                filterAttributes {
                    hierarchyLevel
                    type
                    description
                    attributeType
                }
            }
        }
    `);

    const isValidState = (type, state) => {
      const arr = stateMap.filter(stateItem => {
          const value = stateItem[type].toLowerCase().replaceAll(/\s/g,'');
          return state && value === state.toLowerCase();
      });
      return arr;
    };
    useEffect(() => {
        if (modalData) {
            if (modalData.value) {
                const newList = modalData.value.includes('\n') ? modalData.value.split('\n') : [modalData.value];
                setList(newList);
                setView('manual');
            }
            if (modalData.showAddRow) {
                setView('option');
            }
            setInputs(inputs => ({
                ...inputs,
                key: modalData.data.key || '',
                category: modalData.data.category || '',
                type: modalData.data.type || ''
            }));
            data.categories = {
                enumValues: [
                    {
                        name: 'STATE_LIST'
                    }
                ],
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalData]);

    useEffect(() => {
        if (list) {
            if ((new Set(list)).size !== list.length)
            setList( [...new Set(list)].sort());
        }
    }, [list]);

    useEffect(() => {
        if (stateListData) {
            let importedStateList = [];
            const hasStateList = (arr) => arr.filter(item => item.attributeType === 'HOME_STATE');
            const { servicer, campaign, portfolio } = stateListData;
            if (importType === 'Servicer') {
                if (servicer && servicer.filterAttributes.length) {
                    importedStateList = hasStateList(servicer.filterAttributes);
                } else {
                    setError(true);
                }
            } else if (importType === 'Portfolio') {
                if (portfolio && portfolio.filterAttributes.length) {
                    importedStateList =  hasStateList(portfolio.filterAttributes);
                } else {
                    setError(true);
                }
            } else if (importType === 'Campaign') {
                if (campaign && campaign.filterAttributes.length) {
                    importedStateList = hasStateList(campaign.filterAttributes);
                } else {
                    setError(true);
                }
            }
            // Set States
            if (importedStateList.length) {
                const config = importedStateList[0];
                const stateList = removeParenthesis(config.description).split(',');
                const arr = [];
                stateList.forEach(item => {
                    const stateName = item.toLowerCase().replaceAll(/\s/g,'')
                    const val = isValidState('name', stateName);
                    if (val.length) {
                        arr.push(val[0].value);
                    }
                    setList(arr);
                    setView('manual');
                });
            }
        }
    }, [stateListData])

    const selectState = (e, state) => {
        if(e.target.checked) {
            if (state === 'all') {
                const newList = stateMap.map(item => item.value);
                setList(newList);
            } else {
                setList(prevState => [...prevState, state.value]);
            }
        } else {
            if (state === 'all') {
                setList([]);
            } else {
                const arr = list.filter(item => item !== state.value);
                setList(arr);
            }
        }
    };

    const renderCopyButton = () => (
        <CopyToClipboard text={list}>
            <Box display="flex" alignItems="center" justifyContent="center" style={{margin: '10px 0'}}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FileCopyIcon/>}
                >
                    Copy Raw
                </Button>
            </Box>
        </CopyToClipboard>
    );
    return (
        <Modal
            open={true}
            onClose={closeModal}
        >
            <Container className={classes.modal} maxWidth={false}>
                <Paper className={classes.paper} style={{marginBottom: 0, overflow: 'hidden'}}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Build the State List Below.
                    </Typography>
                    {isError ? (
                        <Alert severity="error" style={{ marginBottom: '5px'}}>
                            Please add values to the highlighted fields!
                        </Alert>
                    ) : false}
                    {view === 'import' ? (
                        <Box>
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                flexDirection={"column"}
                                style={{margin: '10px 0'}}
                                justifyContent={"center"}
                            >
                                {!multipleStates.length ? (
                                    <>
                                        {stateListData && error ? (
                                            <Box style={{ margin: '10px 0'}}>
                                                <Alert severity="error">
                                                    <AlertTitle>{importType}</AlertTitle>
                                                    Please check the id since it's not returning any data or schedules.
                                                </Alert>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Alert severity="warning">Please select the type and id to import the schedule!</Alert>
                                            </Box>
                                        )}
                                    </>
                                ) : null}
                                {!multipleStates.length ? (
                                    <Box
                                        display={"flex"}
                                        alignItems={"center"}
                                        flexDirection={"column"}
                                        style={{margin: '10px 0'}}
                                        justifyContent={"center"}
                                    >
                                        <Box style={{ margin: '10px 0'}}>
                                            <Autocomplete
                                                size="medium"
                                                fullwidth
                                                className={classes.autoComplete}
                                                value={importType || ''}
                                                onChange={(event, value) => {
                                                    setError(false);
                                                    setImportType(value ? value.name : '');
                                                }}
                                                getOptionLabel={option => option.name || option}
                                                options={[{ name: 'Servicer'}, { name: 'Portfolio'}, { name: 'Campaign'}]}
                                                renderInput={
                                                    params =>
                                                        <TextField
                                                            {...params}
                                                            label="Type"
                                                            fullWidth
                                                            error={!importType}
                                                        />
                                                }
                                            />
                                        </Box>
                                        <Box>
                                            <RenderInput
                                                item={{
                                                    label: 'Id',
                                                    key: 'id',
                                                    type: 'text',
                                                    required: false
                                                }}
                                                value={id || ''}
                                                data={data}
                                                disabled={importType === ''}
                                                handleFilterChange={null}
                                                handleInputChange={(e) => {
                                                    setId(e.target.value);
                                                }}
                                                handleSwitchChange={null}
                                            />
                                        </Box>
                                    </Box>
                                ) : null}
                            </Box>
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                style={{margin: '10px 0'}}
                                justifyContent={"center"}>
                                {!multipleStates.length ? (
                                    <Box style={{marginRight: '10px'}}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={id === ''}
                                            onClick={() => {
                                                setError(false);
                                                const variables = {
                                                    campaignId: '',
                                                    servicerId: '',
                                                    portfolioId: ''
                                                };
                                                variables[`${importType.toLowerCase()}Id`] = id;
                                                runQuery({
                                                    variables: {
                                                        ...variables
                                                    }
                                                });
                                            }}
                                        >
                                            Import
                                        </Button>
                                    </Box>
                                ) : null}
                                <Box style={{marginRight: '10px'}}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            setView('schedule');
                                        }}
                                    >
                                        Manually Select States
                                    </Button>
                                </Box>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            closeModal();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                    {view === 'option' ? (
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                            style={{margin: '10px 0'}}
                            justifyContent={"center"}>
                            <Box style={{marginRight: '10px'}}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setView('import');
                                    }}
                                >
                                    Import from Existing
                                </Button>
                            </Box>
                            <Box style={{marginRight: '10px'}}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setView('manual');
                                    }}
                                >
                                    Manually Select States
                                </Button>
                            </Box>
                            <Box>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        closeModal();
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : null}
                    {view === 'manual' ? (
                        <>
                            <ConfigFields
                                inputs={inputs}
                                setInputs={setInputs}
                                data={data}
                                modalData={modalData}
                            />
                            <Grid container spacing={3} style={{padding: '10px'}}>
                                <Grid container spacing={3} style={{padding: '10px'}}>
                                    <Grid item xs={9}>
                                        <RenderInput
                                            item={{
                                                label: 'Manually Enter States with Comma',
                                                key: 'states',
                                                type: 'text',
                                                required: false
                                            }}
                                            value={inputs.states || ''}
                                            data={data}
                                            handleFilterChange={null}
                                            handleInputChange={handleInputChange}
                                            handleSwitchChange={null}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                const states = inputs.states.trim();
                                                if (states.includes(',')) {
                                                    const arr = states.replaceAll(/\s/g,'').split(',');
                                                    const newStates = [];
                                                    arr.forEach(item => {
                                                       const isState = isValidState('value', item);
                                                       if (isState.length) {
                                                           newStates.push(isState[0].value);
                                                       }
                                                    });
                                                    if (newStates.length) {
                                                        setList(prevState => [...prevState, ...newStates]);
                                                    }
                                                } else {
                                                    const newState = isValidState('value', states);
                                                    if (newState.length) {
                                                        setList(prevState => [...prevState, states]);
                                                    }
                                                }
                                            }}
                                        >
                                            Update List
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Grid item xs={9} style={{ padding: 0}}>
                                    <Grid container spacing={3} style={{padding: '10px'}}>
                                        <Grid item xs={12}>
                                            <Button
                                                variant="contained"
                                                className={classes.deleteButton}
                                                onClick={() => {
                                                    setList([]);
                                                }}
                                            >
                                                Clear All
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <FormControl className={classes.formControl}>
                                        <FormGroup>
                                            <Grid container>
                                                <Grid item xs={3}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                defaultChecked={stateMap.length === list.length}
                                                                onChange={(e) => selectState(e, 'all')}
                                                                name='all'
                                                            />
                                                        }
                                                        label={
                                                            <Box
                                                                display="flex"
                                                                alignItems="flex-start"
                                                                justifyContent="space-between"
                                                                flexDirection="column"
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{paddingBottom: '5px'}}
                                                                >
                                                                    Select All
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>
                                                {stateMap.map(item => {
                                                    const isChecked = list.length ? list.includes(item.value) : false;
                                                    return (
                                                        <Grid item xs={3}>
                                                            <FormControlLabel
                                                                key={item.value}
                                                                control={
                                                                    <Checkbox
                                                                        checked={isChecked}
                                                                        onChange={(e) => selectState(e, item)}
                                                                        name={item.value}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box
                                                                        display="flex"
                                                                        alignItems="flex-start"
                                                                        justifyContent="space-between"
                                                                        flexDirection="column"
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{paddingBottom: '5px'}}
                                                                        >
                                                                            {item.value} <br/>
                                                                            <small>{item.name}</small>
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </FormGroup>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3} style={{ padding: 0}}>
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                        <Typography variant="body1">
                                            <strong>JSON State List Preview</strong>
                                            {renderCopyButton()}
                                        </Typography>
                                    </Box>
                                    <Box style={{ height: '550px', overflow: 'scroll'}}>
                                        <SyntaxHighlighter language="json" style={base16AteliersulphurpoolLight}>
                                            {JSON.stringify(list, null, 1)}
                                        </SyntaxHighlighter>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                                style={{margin: '10px 0'}}
                                justifyContent={"center"}>
                                <Box style={{marginRight: '10px'}}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => {
                                            handleSubmit();
                                        }}
                                        disabled={submitLoading}
                                    >
                                        {submitLoading ? (
                                            <CircularProgress color="inherit"/>
                                        ) : (
                                            <>
                                                Save Values
                                            </>
                                        )}
                                    </Button>
                                </Box>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => {
                                            closeModal();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    ) : null}
                </Paper>
            </Container>
        </Modal>
    );
};

export default ConfigStateModal;
